import { PLATFORM_FEE_STATEMENT, PROCESSING_FEE_DISCLOSURE } from '@/data/pricing-content'
import { readEnv } from '@/lib/env'

export const SITE_NAME = 'KindLumen'
export const DEFAULT_OG_IMAGE_PATH = '/opengraph-image-v2.png'
export const DEFAULT_DESCRIPTION =
  'KindLumen is a free Stripe donation form for websites that support custom embeds. Create a branded form, connect your own Stripe account, and add one-time and monthly giving to WordPress, Squarespace, Wix, Webflow, or a custom site — with no platform fees.'

const defaultKeywords = [
  'free Stripe donation form',
  'free donation form',
  'Stripe donation form',
  'free donation form for website',
  'embed Stripe donation form',
  'Stripe donation form for nonprofits',
  'embeddable donation form',
  'recurring donation form',
  'donation widget for websites',
]

export type SeoPageConfig = {
  path: string
  title: string
  description: string
  keywords: string[]
}

export type CreateMetadataOptions = SeoPageConfig & {
  imagePath?: string
  noIndex?: boolean
  openGraphType?: 'website' | 'article'
}

export type ArticleMetadataExtras = {
  publishedTime?: string
  modifiedTime?: string
  authors?: string[]
  tags?: string[]
  imageWidth?: number
  imageHeight?: number
  imageAlt?: string
}

export type AppMetadata = {
  title: string
  description: string
  keywords: string[]
  canonical: string
  noIndex: boolean
  openGraph: {
    type: 'website' | 'article'
    url: string
    title: string
    description: string
    siteName: string
    locale: string
    image: {
      url: string
      width: number
      height: number
      alt: string
    }
    publishedTime?: string
    modifiedTime?: string
    authors?: string[]
    tags?: string[]
  }
  twitter: {
    card: 'summary_large_image'
    title: string
    description: string
    image: string
  }
}

function normalizeSiteUrl(value: string) {
  const trimmed = value.trim()
  const withProtocol = /^https?:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`
  return withProtocol.replace(/\/+$/, '')
}

export function getSiteUrl() {
  const fromEnv =
    readEnv('VITE_SITE_URL') ??
    readEnv('SITE_URL') ??
    readEnv('VERCEL_PROJECT_PRODUCTION_URL') ??
    readEnv('VERCEL_URL')

  return normalizeSiteUrl(fromEnv ?? 'http://localhost:3000')
}

export function getMetadataBase() {
  return new URL(getSiteUrl())
}

export function absoluteUrl(path = '/') {
  if (/^https?:\/\//.test(path)) {
    return new URL(path)
  }
  return new URL(path, getMetadataBase())
}

export function createMetadata({
  path,
  title,
  description,
  keywords,
  imagePath = DEFAULT_OG_IMAGE_PATH,
  noIndex = false,
  openGraphType = 'website',
}: CreateMetadataOptions): AppMetadata {
  const imageUrl = absoluteUrl(imagePath).toString()
  const canonical = absoluteUrl(path).toString()

  return {
    title,
    description,
    keywords: Array.from(new Set([...defaultKeywords, ...keywords])),
    canonical,
    noIndex,
    openGraph: {
      type: openGraphType,
      url: canonical,
      title,
      description,
      siteName: SITE_NAME,
      locale: 'en_US',
      image: {
        url: imageUrl,
        width: 1200,
        height: 630,
        alt: title,
      },
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      image: imageUrl,
    },
  }
}

export function withArticleExtras(metadata: AppMetadata, extras: ArticleMetadataExtras): AppMetadata {
  return {
    ...metadata,
    openGraph: {
      ...metadata.openGraph,
      type: 'article',
      publishedTime: extras.publishedTime,
      modifiedTime: extras.modifiedTime,
      authors: extras.authors,
      tags: extras.tags,
      image: {
        ...metadata.openGraph.image,
        width: extras.imageWidth ?? metadata.openGraph.image.width,
        height: extras.imageHeight ?? metadata.openGraph.image.height,
        alt: extras.imageAlt ?? metadata.openGraph.image.alt,
      },
    },
  }
}

export const marketingSeoPages = {
  agencies: {
    path: '/agencies',
    title: 'Donation Forms for Web Agencies & Developers | KindLumen',
    description:
      'Build branded donation forms for nonprofit client websites. Create a client workspace, invite the nonprofit to connect its own Stripe account, and embed with KindLumen.',
    keywords: ['donation platform for web design agencies', 'donation forms for developers', 'nonprofit client donation form', 'agency Stripe donation setup'],
  },
  home: {
    path: '/',
    title: 'Free Stripe Donation Form for Your Website | KindLumen',
    description:
      'Add a free Stripe donation form to your existing website. Connect your own Stripe account, paste the generated embed snippet, and collect unlimited one-time and monthly donations with no KindLumen platform fee.',
    keywords: [
      'free Stripe donation form',
      'free donation form for website',
      'add donation form to website',
      'Stripe donation form for nonprofits',
      'recurring donation form',
    ],
  },
  features: {
    path: '/features',
    title: 'Free Stripe Donation Form — Features & Embed | KindLumen',
    description:
      'Everything in KindLumen’s free Stripe donation form: branded one-time and recurring giving, campaign pages, popups, and a copy-paste embed that connects to your own Stripe account.',
    keywords: [
      'free Stripe donation form features',
      'embeddable donation form',
      'donation widget for websites',
      'embed donation form website',
      'Stripe donation form embed code',
    ],
  },
  pricing: {
    path: '/pricing',
    title: 'Free Stripe Donation Form — $0 Platform Fee | KindLumen',
    description: `KindLumen is a free Stripe donation form. ${PLATFORM_FEE_STATEMENT} Optional transaction-cost coverage is calculated from a disclosed processing-rate estimate with the aim of preserving the intended gift.`,
    keywords: [
      'free Stripe donation form',
      'free donation form no monthly fee',
      'donation form pricing',
      'zero platform fee donation form',
      'Donorbox alternative for Stripe users',
    ],
  },
  about: {
    path: '/about',
    title: 'About KindLumen — The Free Stripe Donation Form',
    description:
      'Why KindLumen builds a focused Stripe donation form for existing websites, with no website rebuild, no KindLumen platform fee, and funds handled through the organization’s connected Stripe account.',
    keywords: [
      'free Stripe donation form',
      'Stripe donation form for nonprofits',
      'embeddable donation form',
    ],
  },
  solutions: {
    path: '/solutions',
    title: 'Free Stripe Donation Forms for Nonprofits | KindLumen',
    description:
      'How nonprofits, faith-based groups, and web agencies add KindLumen’s free Stripe donation form without rebuilding their websites. Donor and subscription migration, when needed, is a separate project.',
    keywords: [
      'free donation form for nonprofits',
      'stripe donation form for mosques',
      'donation forms for client websites',
      'recurring donations for faith-based nonprofits',
    ],
  },
  resources: {
    path: '/resources',
    title: 'Donation Platform Comparisons, Guides & Tools | KindLumen',
    description:
      'Source-backed donation platform comparisons, practical website setup guides, and free fundraising tools from KindLumen.',
    keywords: [
      'donation platform comparisons',
      'how to add a donation form to your website',
      'fundraising website guides',
      'free fundraising tools',
    ],
  },
  blog: {
    path: '/blog',
    title: 'Stripe Donation Form Blog — Guides & Comparisons | KindLumen',
    description:
      'Guides from KindLumen on accepting Stripe donations, embedding forms on nonprofit websites, and evaluating donation platform tradeoffs.',
    keywords: [
      'how to add a donation form to your website',
      'free Stripe donation form',
      'WordPress donation form',
      'Donorbox alternative with Stripe',
    ],
  },
  docs: {
    path: '/docs',
    title: 'Free Stripe Donation Form Docs & Setup | KindLumen',
    description:
      'Documentation for KindLumen’s free Stripe donation form: connecting Stripe, embedding the form on your website, and managing recurring giving and receipts.',
    keywords: [
      'embed donation form website',
      'how to connect Stripe donation form',
      'recurring donation form setup',
    ],
  },
  integrations: {
    path: '/integrations',
    title: 'Embed a Free Stripe Donation Form on Your Website | KindLumen',
    description:
      'Embed KindLumen’s free Stripe donation form on WordPress or on website plans that allow script-based custom HTML. Use the generated snippet or the free WordPress plugin.',
    keywords: [
      'embed Stripe donation form',
      'donation form for WordPress',
      'donation form for Squarespace',
      'donation form for Webflow',
      'donation form embed code',
    ],
  },
  wordpressPlugin: {
    path: '/wordpress-plugin',
    title: 'Free WordPress Donation Plugin for Stripe | KindLumen',
    description:
      'KindLumen – Free Stripe Donation Forms is a free WordPress donation plugin with guided account connection, Donate buttons and forms, campaign goals, and no raw card data stored in WordPress. Stripe-backed forms support one-time and monthly giving.',
    keywords: [
      'WordPress donation plugin',
      'Stripe donation plugin for WordPress',
      'free WordPress donation form plugin',
      'WordPress donate button',
      'free Stripe donation form',
    ],
  },
  live: {
    path: '/live',
    title: 'Live Fundraising Appeals on the Big Screen | KindLumen',
    description:
      'Run a live appeal for your masjid, school, charity or gala: a big screen with a live total, volunteers tapping raised hands, donors giving by QR code, and pledge reminders after the night. No platform fees.',
    keywords: [
      'live fundraising appeal',
      'live donation screen',
      'fundraising thermometer for events',
      'live pledge tracker',
      'masjid fundraising night',
      'gala fundraising screen',
      'free Stripe donation form',
    ],
  },
  pledgeCounter: {
    path: '/tools/pledge-counter',
    title: 'Free Fundraising Pledge Counter | KindLumen',
    description:
      'A free pledge counter for live fundraisers: tap an amount each time someone pledges and watch the total, the number of pledges, and the breakdown by amount update on screen. No sign-up — it saves on your device and exports to CSV.',
    keywords: [
      'pledge counter',
      'fundraising pledge tracker',
      'fundraiser tally counter',
      'live fundraising total',
      'free Stripe donation form',
    ],
  },
  tools: {
    path: '/tools',
    title: 'Free Fundraising Tools & Calculators | KindLumen',
    description:
      'Free tools for nonprofits, churches, Islamic centers, and community groups: a live pledge counter, zakat calculator, donation fee calculator, gift range chart, and tithe calculator. No sign-up; they run in your browser.',
    keywords: ['free fundraising tools', 'nonprofit calculators', 'church giving calculator', 'zakat calculator', 'free Stripe donation form'],
  },
  zakatCalculator: {
    path: '/tools/zakat-calculator',
    title: 'Free Zakat Calculator (Gold, Silver, Cash) | KindLumen',
    description:
      'Calculate your zakat: cash, gold and silver by weight or value, investments, business assets, and debts, with nisab by gold or silver and the 2.5% rate. Private — nothing is saved or sent. For Muslims and Islamic centers.',
    keywords: ['zakat calculator', 'nisab calculator', 'zakat on gold', 'zakat on savings', 'masjid donation form'],
  },
  donationFeeCalculator: {
    path: '/tools/donation-fee-calculator',
    title: 'Donation Fee Calculator: Stripe, PayPal & More | KindLumen',
    description:
      'Compare what your organization keeps from a donation on Stripe, PayPal, Donorbox, Zeffy, Givebutter, and KindLumen — platform fees, processing fees, yearly totals, and what to ask so a gift nets your target.',
    keywords: ['donation fee calculator', 'Stripe fee calculator donations', 'donation processing fees', 'Donorbox fees', 'free Stripe donation form'],
  },
  giftRangeChart: {
    path: '/tools/gift-range-chart',
    title: 'Gift Range Chart Calculator for Campaigns | KindLumen',
    description:
      'Enter a campaign goal and get a gift range chart: how many gifts at which levels, how many prospects to ask, and how far each level takes you. Free, exportable, built for capital and building campaigns.',
    keywords: ['gift range chart', 'gift range chart calculator', 'capital campaign gift pyramid', 'fundraising gift table', 'campaign goal tracker'],
  },
  titheCalculator: {
    path: '/tools/tithe-calculator',
    title: 'Free Tithe Calculator (Weekly, Monthly, Yearly) | KindLumen',
    description:
      'Work out your tithe per paycheck, per month, and per year at 10% or any percentage, with an offering on top. No sign-up, nothing saved. From KindLumen, the free Stripe donation form for churches.',
    keywords: ['tithe calculator', 'tithing calculator', '10 percent tithe', 'church giving calculator', 'online giving for churches'],
  },
  donationReceiptGenerator: {
    path: '/tools/donation-receipt-generator',
    title: 'Donation Receipt & Thank-You Letter Generator | KindLumen',
    description:
      'Draft a donation receipt, thank-you letter, or year-end statement with common acknowledgment language based on IRS Publication 1771. Add your letterhead, review the details, then download a PDF or prepare an email. Free, no sign-up.',
    keywords: ['donation receipt template', 'donation receipt generator', 'nonprofit thank you letter template', 'year end giving statement', '501c3 donation acknowledgment'],
  },
  recurringGivingCalculator: {
    path: '/tools/recurring-giving-calculator',
    title: 'Recurring Giving Calculator for Nonprofits | KindLumen',
    description:
      'See what a monthly gift adds up to over 1, 3, 5, and 10 years, compare it with a one-time gift, and project a monthly giving program with new donors and churn month by month. Free, exportable.',
    keywords: ['recurring giving calculator', 'monthly donation calculator', 'monthly giving program projection', 'donor churn calculator', 'recurring donations nonprofit'],
  },
  qrPoster: {
    path: '/tools/qr-poster',
    title: 'Free Scan-to-Donate QR Code Poster Generator | KindLumen',
    description:
      'Make a “Scan to donate” poster, table tent, or hand-out cards for a donation link. Add your name, logo, and brand color; export a print-ready PDF or PNG, or download the QR code as SVG. Free, no sign-up.',
    keywords: ['qr code donation poster', 'scan to donate qr code', 'donation qr code generator', 'church giving qr code', 'fundraising qr code poster', 'masjid donation qr code'],
  },
  yearEndStatements: {
    path: '/tools/year-end-statements',
    title: 'Bulk Year-End Donation Statements from a CSV | KindLumen',
    description:
      'Map a CSV of donation records and draft one year-end statement per donor with common acknowledgment language based on IRS Publication 1771, your letterhead, and gift-by-gift detail. Review before sending. Free, no sign-up.',
    keywords: ['year end donation statements', 'annual giving statement generator', 'bulk donation receipts csv', 'church contribution statements', 'nonprofit year end tax letters', '501c3 annual statement template'],
  },
  sponsorshipTracker: {
    path: '/tools/sponsorship-tracker',
    title: 'Event Sponsorship & Table Tracker for Galas | KindLumen',
    description:
      'Track gala and fundraiser sponsorships: tiers, sponsors, pledged vs paid, seats and tables sold, and progress to goal. Saves on your device, exports to CSV. Free, no sign-up.',
    keywords: ['sponsorship tracker', 'gala sponsorship tiers template', 'event table tracker', 'fundraising dinner sponsors', 'nonprofit event planning'],
  },
  contactSales: {
    path: '/contact-sales',
    title: 'Contact KindLumen — Free Stripe Donation Forms',
    description:
      'Talk with KindLumen about embedding a free Stripe donation form, replacing an existing donate page, or rolling out forms across client sites.',
    keywords: [
      'free Stripe donation form',
      'Donorbox alternative',
      'Fundraise Up alternative',
      'Stripe Payment Links donation alternative',
    ],
  },
} satisfies Record<string, SeoPageConfig>

export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: getSiteUrl(),
    logo: absoluteUrl('/favicon.png').toString(),
    description: DEFAULT_DESCRIPTION,
  }
}

export function getWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: getSiteUrl(),
    description: DEFAULT_DESCRIPTION,
  }
}

export function getProductSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['Product', 'SoftwareApplication'],
    name: SITE_NAME,
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
    category: 'Donation form software',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    isAccessibleForFree: true,
    url: absoluteUrl('/').toString(),
    image: absoluteUrl(DEFAULT_OG_IMAGE_PATH).toString(),
    description:
      'Free Stripe donation forms for websites that support custom embeds. Create a branded one-time and monthly donation form, connect your own Stripe account, and add it to WordPress, Squarespace, Wix, Webflow, or a custom site. KindLumen charges no setup, monthly, or platform fees, has no limits on forms, donations, or donors, never asks donors to tip, and never holds donated funds.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      description: `${PLATFORM_FEE_STATEMENT} Donors may optionally cover transaction costs using a disclosed processing-rate estimate intended to help preserve the selected gift. ${PROCESSING_FEE_DISCLOSURE}`,
    },
  }
}

export function getBreadcrumbSchema(items: Array<{ name: string; path: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path).toString(),
    })),
  }
}

export function getFaqSchema(items: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}

export function getBlogSchema({
  title,
  description,
  path,
}: {
  title: string
  description: string
  path: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: title,
    description,
    url: absoluteUrl(path).toString(),
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: getSiteUrl(),
    },
  }
}

export function getBlogPostingSchema({
  title,
  description,
  path,
  publishedAt,
  updatedAt,
  author,
  keywords,
  image,
}: {
  title: string
  description: string
  path: string
  publishedAt: string
  updatedAt: string
  author: { id: string; name: string }
  keywords: string[]
  image?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    datePublished: publishedAt,
    dateModified: updatedAt,
    mainEntityOfPage: absoluteUrl(path).toString(),
    image: absoluteUrl(image ?? DEFAULT_OG_IMAGE_PATH).toString(),
    keywords,
    author: {
      '@type': 'Person',
      name: author.name,
      url: absoluteUrl(`/editorial-standards#${author.id}`).toString(),
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: absoluteUrl('/favicon.png').toString(),
      },
    },
  }
}
